import mapboxgl from 'mapbox-gl';
import { getBackendUrl, getMapCenter, getMapInitialZoom } from '../services/configService';
import { loadSavedViewport } from './mapViewportPersistence';
import { attachAffMapActions } from './liveLayers/providers/affMapActions';

declare global {
  interface Window {
    map?: mapboxgl.Map;
  }
}

// Empty style used when no caller URL and no per-user cache exists.
// Renders nothing for the base — TMS/WMS background layers paint over a
// transparent canvas, and CSS background fills in any uncovered area. Avoids
// fetching streets-v11 just to throw it away when a custom background loads.
// Glyphs URL is required so symbol layers (labels) can resolve fonts.
const BLANK_STYLE: mapboxgl.StyleSpecification = {
  version: 8,
  sources: {},
  layers: [],
  glyphs: 'mapbox://fonts/mapbox/{fontstack}/{range}.pbf',
};

/**
 * Read the per-user cached background styleUrl that LayerDrawerProvider
 * writes on every layer-state save. Lets the map render with the user's
 * last background on first try, even before canReadLayers / API resolves.
 */
const readCachedBackgroundUrl = (userId?: string | null): string | null => {
  if (!userId) return null;
  try {
    return localStorage.getItem(`em3-bg-url:${userId}`);
  } catch {
    return null;
  }
};

/**
 * Initialize the Mapbox map instance.
 * @param container - The HTML container element for the map
 * @param initialStyleUrl - Optional style URL from database. If absent, falls
 *   back to the user's cached background, then a blank style.
 */
export const initializeMap = async (
  container: HTMLDivElement | null,
  initialStyleUrl?: string,
  userId?: string | null
): Promise<{ map: mapboxgl.Map | null, initialBackground: string | null }> => {
  if (!container) return { map: null, initialBackground: null };

  // Resolution order: caller-provided URL → per-user cached URL → blank style
  const cachedUrl = readCachedBackgroundUrl(userId);
  const resolvedUrl = initialStyleUrl || cachedUrl || null;
  const mapStyle: string | mapboxgl.StyleSpecification =
    resolvedUrl ?? BLANK_STYLE;

  // Get backend URL for credential inclusion
  const backendUrl = getBackendUrl() || '';
  const savedViewport = loadSavedViewport(userId);
  const mapCenter = getMapCenter();
  const mapZoom = getMapInitialZoom();

  // Clear the container
  container.innerHTML = '';

  const map = new mapboxgl.Map({
    container,
    style: mapStyle,
    center: savedViewport
      ? [savedViewport.lng, savedViewport.lat]
      : [mapCenter.lon, mapCenter.lat],
    zoom: savedViewport ? savedViewport.zoom : mapZoom,
    minZoom: 4,
    preserveDrawingBuffer: true,
    dragRotate: false,
    touchPitch: false,
    transformRequest: (url: string) => {
      // Include auth for requests to our backend (geoserver proxy, Nomad tiles, etc.)
      // Mapbox fetches tiles directly — must attach Bearer token from localStorage
      if (backendUrl && url.startsWith(backendUrl)) {
        const token = localStorage.getItem('access_token');
        const headers: Record<string, string> = {};
        if (token) headers['Authorization'] = `Bearer ${token}`;
        return { url, credentials: 'include' as const, headers };
      }
      return { url };
    }
  });

  // Expose the map instance to the window object
  window.map = map;

  // Wait for the map style to load before adding terrain. Registered before
  // the AFF map actions: style.load listeners run in registration order, and
  // AFF's redraws the 3D models, which need the terrain back first (#820 g).
  map.on('style.load', () => {
    map.addSource('mapbox-dem', {
      'type': 'raster-dem',
      'url': 'mapbox://mapbox.mapbox-terrain-dem-v1',
      'tileSize': 512,
      'maxzoom': 14
    });

    // Add the DEM source as a terrain layer with exaggerated height
    map.setTerrain({
      'source': 'mapbox-dem',
      'exaggeration': 2
    });
  });

  // AFF map actions (Locate / Show Track / Clear Tracks) belong to the map, not
  // to the AFF layer. Attaching here means they work whether or not the AFF
  // live layer is switched on — previously they were wired inside the layer's
  // marker rendering and silently did nothing with the layer off (#779).
  attachAffMapActions(map);

  // Return the style URL used (null when we fell back to the blank spec)
  return { map, initialBackground: resolvedUrl };
};